import { Injectable } from '@angular/core';
import PouchDB from 'pouchdb-browser';
import { DateTime } from 'luxon';

import { DeferredPromise } from '@cf-shared/shared-util';
import { isPouchConflict, isPouchNotFound } from '@cf-shared/data-model/pouch';
import { getLocalAdapterOpts } from './local-database-engine';
import { AuthService } from 'services/auth.service';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { LoggingService } from 'services/logging.service';
import {
  makeLocalAdapterFaultReporter, makeLocalCorruptionReporter,
} from './pouchdb-adapter-capacitor-sqlite/local-corruption-reporter';
import { TelemetryService } from 'services/telemetry.service';
import { PubSubService } from 'services/pubsub.service';
import { ConnectionState, connectionStateChangedEvent, OnlineStateService } from 'services/online-state.service';
import { isOfflineOrTransientNetworkError } from 'services/error-patterns';
import { ImageUploadHttpError, putImageToSignedUrl } from 'services/image-upload-helpers';
import { base64ToBlob, isObjectAlreadyStoredError, shouldDropOutboxItem, shouldReportOutboxPutRejection } from 'services/image-upload-outbox-rules';
import { STORAGE_WRITE_OBJECT_EXISTS_HTTP_STATUS } from '@cf-shared/storage-interface';
import { describeError, describeErrorForLog } from '@cf-shared/error-describe';

// #3989 (offline Phase 2, umbrella F4) — durable offline outbox for image
// uploads. When ImageService.uploadImage fails with a transient/offline error,
// the image bytes are queued here instead of lost: bytes + GCS object key +
// content type persist in a dedicated local-only PouchDB (never synced; the
// bytes live in the doc's bytesBase64 field — NOT as an attachment, because
// the capacitor-sqlite adapter has none). Because download URLs are deterministic
// public object URLs (see image-url-helpers.ts), the caller already saved its
// document with the final URL — this outbox only owes the server the bytes.
//
// The drain runs on every Offline→Online transition (subscribed below) and
// once after login (kicked from app.component.ts, cold + warm paths). Items
// that keep failing are dropped by the pure give-up rule in
// image-upload-outbox-rules.ts, with telemetry so drops are observable.
//
// Sign-out safety: a non-empty outbox counts as unflushed changes in the
// sign-out guard (sign-out-guard-flow.ts, #3988 F1). The sign-out wipe then
// deletes this DB on every platform. On SQLite devices (iOS native, migrated
// Android) that only happens because sqliteDatabaseNamesForSignOut in
// reload-teardown.ts names it explicitly: this is a raw PouchDB, not one the
// database provider lists (#5269). "Delete Private Data" empties the outbox
// itself, through clearAllQueuedAsync below. Both: design-docs/sync-integrity.md,
// "The change-tracking flags".

// One queued upload. The doc _id is the GCS object key itself (unique per
// size variant); the image bytes live in `bytesBase64` as a PLAIN DOC FIELD,
// NOT as a PouchDB attachment. This is deliberate: the custom
// pouchdb-adapter-capacitor-sqlite has no attachment support at all, so a
// Blob attachment silently JSON-serializes to `{}` and the bytes are LOST on
// iOS native (found during the #3989 on-device verification, 2026-07-12 —
// the drain then fails forever on getAttachment and the photo never
// uploads). A base64 string field round-trips through every adapter.
interface OutboxDoc {
  _id: string;
  _rev?: string;
  contentType: string;
  createdAt_utc: string; // ISO timestamp, drives oldest-first drain order + the age cap
  attempts: number;      // reachable-drain failures so far (offline skips do not count)
  // base64-encoded image bytes (no data-URL prefix). optional because docs
  // queued by the first shipped Phase 2 build stored an attachment instead —
  // see readQueuedBlobAsync for the legacy fallback.
  bytesBase64?: string;
}

/**
 * The outbox's local PouchDB name.
 *
 * Exported because the sign-out teardown has to delete it explicitly (#4981 phase 3,
 * R13). This database does NOT go through `DatabaseWrapperProvider`, so it is absent
 * from the provider's list that the teardown's SQLite deleter walks — see the note
 * beside `localDatabaseNames` in `app.component.ts`.
 */
export const OUTBOX_DB_NAME = 'image_upload_outbox';
// legacy only (first Phase 2 build): the attachment name that build used
const ATTACHMENT_NAME = 'bytes';

@Injectable({ providedIn: 'root' })
export class ImageUploadOutboxService {
  // opened lazily on first use (an enqueue, a drain, or a pending-count read).
  // note: the reconnect-drain subscription below means most sessions still
  // open the (empty) DB once — that open is cheap; lazy just keeps it off the
  // boot-critical path
  private db: PouchDB.Database<OutboxDoc> | null = null;

  // single-flight guard, same pattern as AuthService.couchLoginInProgress: a
  // second drain trigger while one is running just awaits the running one
  private drainInProgress: DeferredPromise<void> | null = null;

  // #3989 follow-up (queued offline photos must display immediately): local
  // preview URLs for queued items, keyed by the GCS object key. While a photo
  // sits in the outbox its public URL cannot load (the bytes have not reached
  // the server), so ImageService's display helpers substitute these object
  // URLs instead. Entries are added on enqueue, preloaded once per session
  // from the DB (kicked from app.component next to the post-login drain), and
  // removed + revoked when the item leaves the outbox (drained or dropped).
  // No sign-out handling is needed: sign-out ends in reloadAppAsync, and a
  // full page reload discards this map together with its object URLs.
  private localPreviewUrls = new Map<string, string>();
  private previewsPreloaded = false;

  constructor(
    private auth: AuthService,
    private cloudFunctions: CloudFunctionsService,
    private logging: LoggingService,
    private telemetry: TelemetryService,
    private pubsub: PubSubService,
    private onlineState: OnlineStateService,
  ) {
    // drain whenever connectivity returns. the subscription lives for the app's
    // lifetime (root singleton), so no teardown is needed.
    this.pubsub.subscribe(connectionStateChangedEvent, (state: ConnectionState) => {
      if (state === ConnectionState.Online) void this.drainAsync();
    });
  }

  /** Open (once) and return the outbox DB. Lazy so the open happens off the
   * boot-critical path, on the first enqueue/drain/count that needs it. */
  private getDb(): PouchDB.Database<OutboxDoc> {
    if (!this.db) {
      this.db = new PouchDB<OutboxDoc>(OUTBOX_DB_NAME, {
        auto_compaction: true,
        ...getLocalAdapterOpts(
          makeLocalCorruptionReporter(this.logging, 'image-upload-outbox'),
          makeLocalAdapterFaultReporter(this.logging, 'image-upload-outbox'),
        ),
      });
    }
    return this.db;
  }

  /** Queue image bytes for upload to `path` once the connection returns. The
   * caller has already received its final download URL (deterministic public
   * object URL), so this only stores what the server is still owed. */
  async enqueueAsync(path: string, blob: Blob, contentType: string): Promise<void> {
    // plain doc field, NOT an attachment — see the OutboxDoc comment (the
    // capacitor-sqlite adapter has no attachment support and loses the bytes)
    const bytesBase64 = await blobToBase64Async(blob);
    try {
      await this.getDb().put({
        _id: path,
        contentType,
        createdAt_utc: DateTime.utc().toISO(),
        attempts: 0,
        bytesBase64,
      });
    } catch (err) {
      // expected: PouchDB 'conflict' (409) when this object key is already
      // queued — the doc _id IS the key, so a duplicate enqueue means the
      // photo is already safely in the outbox. treat it as success; throwing
      // here would escape uploadImage's enqueue catch and turn a queued photo
      // into a loud upload failure.
      if (isPouchConflict(err)) {
        console.log('ImageUploadOutbox: item already queued, keeping the existing entry', path);
        // the queued bytes are the same photo — make sure a preview exists
        this.addLocalPreview(path, blob);
        return;
      }
      throw err;
    }
    this.addLocalPreview(path, blob);
    this.telemetry.trackEvent('image_outbox_enqueued', {
      path_prefix: pathPrefix(path),
      bytes: blob.size,
    });
  }

  /** Local preview URL for a queued item, or undefined when the path is not
   * queued (or the preload has not seen it yet). Sync on purpose: the
   * ImageService display helpers run in template bindings. */
  getLocalPreviewUrl(path: string): string | undefined {
    return this.localPreviewUrls.get(path);
  }

  /** Whether this object key is still queued. Checks the preview map first
   * (fast path), then the DB — the map may not be loaded yet when an <img>
   * error fires early in the session. */
  async hasQueuedAsync(path: string): Promise<boolean> {
    if (this.localPreviewUrls.has(path)) {
      return true;
    }
    try {
      await this.getDb().get(path);
      return true;
    } catch (err) {
      if (isPouchNotFound(err)) {
        return false;
      }
      throw err;
    }
  }

  /** Load preview URLs for every queued item left over from a previous
   * session. One-shot per session; kicked from app.component next to the
   * post-login drain so previews exist before (most) images render. */
  async preloadPreviewsAsync(): Promise<void> {
    if (this.previewsPreloaded) {
      return;
    }
    try {
      const db = this.getDb();
      const all = await db.allDocs({ include_docs: true });
      for (const row of all.rows) {
        // enqueueAsync may have populated this path already in this session
        if (row.doc == null || this.localPreviewUrls.has(row.id)) {
          continue;
        }
        try {
          const blob = await this.readQueuedBlobAsync(row.doc);
          if (blob) {
            this.addLocalPreview(row.id, blob);
          }
          // a null (legacy native item with no stored bytes — see
          // readQueuedBlobAsync) gets no preview; the drain drops it later
        } catch (err) {
          // one unreadable item must not block previews for the rest —
          // report it and continue; the drain handles the item's fate
          this.reportPreloadProblem('item', err);
        }
      }
      // mark done only after a successful pass: a transient boot-time DB
      // failure below must not disable the preload for the whole session —
      // the next kick (e.g. warm resume) retries. per-item failures above do
      // not block completion; their items' fate belongs to the drain.
      this.previewsPreloaded = true;
    } catch (err) {
      // previews are a display aid — a failed preload must not break anything,
      // but it must reach a future debugger (images will look broken offline)
      this.reportPreloadProblem('whole-preload', err);
    }
  }

  /** One reporting shape for the two preload failure modes; the stage field
   * lets a debugger tell "one photo unreadable" from "preload never ran". */
  private reportPreloadProblem(stage: 'item' | 'whole-preload', err: unknown) {
    this.logging.trackSilentProblem('ImageOutboxPreviewPreloadFailed', {
      stage,
      name: err instanceof Error ? err.name : undefined,
      message: describeError(err),
    });
  }

  /**
   * Delete every queued upload WITHOUT uploading it, and report how it went.
   * The one caller is "Delete Private Data" (`purgeAccountAsync` in
   * `client/src/pages/user-profile/user-profile.ts`), and it is the only place
   * where throwing these bytes away is the right thing to do. Why that is the
   * right answer, and why the count is reported at info tier rather than through
   * `dropItemAsync`'s problem-tier data-loss path, is in
   * `design-docs/sync-integrity.md`, "The change-tracking flags".
   *
   * The docs are REMOVED rather than the database destroyed: the app keeps
   * running until the sign-out reload, and a destroyed handle would make every
   * later call on this service throw.
   *
   * One race is accepted rather than locked out: a drain already in flight can
   * finish uploading the item it is holding. Waiting for it would upload MORE,
   * not less, and the items it has not reached are still deleted.
   */
  async clearAllQueuedAsync(): Promise<{ removed: number; failed: number }> {
    const db = this.getDb();
    // no include_docs: a queued item's bytes are a base64 string in the doc
    // itself, and reading all of them in would be the whole outbox in memory for
    // a pass that needs only the id and the revision `remove` asks for.
    const all = await db.allDocs();
    let removed = 0;
    let failed = 0;
    for (const row of all.rows) {
      const outcome = await this.deleteQueuedItemForPurgeAsync(row.id, row.value.rev);
      if (outcome === 'removed') {
        removed += 1;
      } else if (outcome === 'failed') {
        failed += 1;
      }
      // idempotent, so it is right on the already-gone path too: whatever beat us
      // to the removal has already revoked this preview.
      this.removeLocalPreview(row.id);
    }
    return { removed, failed };
  }

  /**
   * Delete one queued item on behalf of `clearAllQueuedAsync`, and say which of
   * three things happened: this call `removed` it, something else had already
   * taken it out of the outbox (`already-gone`), or it is `failed` — still
   * queued, and nothing here can shift it.
   *
   * The three-way answer exists because a PouchDB `conflict` does NOT mean the
   * item is gone. The revision comes from the `allDocs` snapshot, and a drain
   * running alongside this rewrites a queued document IN PLACE on a
   * non-offline failure (`doc.attempts += 1; db.put(doc)` in `drainInnerAsync`).
   * When that lands in between, the delete is refused and the photo is still
   * there. Reading a conflict as "already gone" would leave the outbox
   * non-empty while reporting a clean discard — and a non-empty outbox is what
   * puts the sign-out guard's warning dialog back, which is the whole thing
   * this clear exists to prevent.
   *
   * So a conflict is resolved rather than guessed at: re-read the item and try
   * once more at its current revision. Bounded to that one retry, because the
   * only way to lose the race twice is a drain rewriting the same item again,
   * and a second refusal is honestly reported rather than chased.
   */
  private async deleteQueuedItemForPurgeAsync(
    path: string, rev: string,
  ): Promise<'removed' | 'already-gone' | 'failed'> {
    try {
      await this.getDb().remove(path, rev);
      return 'removed';
    } catch (err) {
      // expected: pouch not_found when a drain uploaded or dropped this item
      // between the listing and here. It has left the outbox, which is what this
      // method wanted, so it is neither a removal of ours nor a failure.
      if (isPouchNotFound(err)) {
        return 'already-gone';
      }
      if (!isPouchConflict(err)) {
        console.error('ImageUploadOutbox: could not delete a queued item during a purge',
          path, describeErrorForLog(err));
        return 'failed';
      }
    }
    // the one retry: the revision moved under us, so ask what it is now.
    try {
      const current = await this.getDb().get(path);
      await this.getDb().remove(current);
      return 'removed';
    } catch (retryErr) {
      // expected: pouch not_found when the drain finished the item between the
      // conflict above and this read — again, it has left the outbox.
      if (isPouchNotFound(retryErr)) {
        return 'already-gone';
      }
      console.error('ImageUploadOutbox: a queued item is still there after a purge retry',
        path, describeErrorForLog(retryErr));
      return 'failed';
    }
  }

  /** Cancel a queued upload because the user removed the photo: delete the
   * outbox doc and revoke its local preview. Returns true when a queued item
   * existed for this path, false when nothing was queued (the caller then
   * deletes the already-uploaded object on the server instead). */
  async removeQueuedAsync(path: string): Promise<boolean> {
    try {
      const doc = await this.getDb().get(path);
      await this.getDb().remove(doc);
    } catch (err) {
      // expected: pouch not_found when this path was never queued (or already
      // drained) — the photo lives on the server, not in the outbox
      if (isPouchNotFound(err)) {
        return false;
      }
      throw err;
    }
    this.removeLocalPreview(path);
    return true;
  }

  /** The queued image bytes of one outbox doc, or null when they cannot be
   * read. New docs carry `bytesBase64`; docs queued by the first shipped
   * Phase 2 build stored a PouchDB attachment instead — that still reads fine
   * on web (IndexedDB), but on iOS native the capacitor-sqlite adapter never
   * persisted those bytes, so the legacy read fails there and null is
   * returned (the item's photo is unrecoverable). */
  private async readQueuedBlobAsync(doc: OutboxDoc): Promise<Blob | null> {
    if (doc.bytesBase64) {
      return base64ToBlob(doc.bytesBase64, doc.contentType);
    }
    try {
      return await this.getDb().getAttachment(doc._id, ATTACHMENT_NAME) as Blob;
    } catch (err) {
      // expected: pouch not_found when the attachment bytes were never
      // persisted (legacy native docs — the capacitor-sqlite adapter stored
      // nothing). null tells the drain to drop the item as data loss.
      if (isPouchNotFound(err)) {
        return null;
      }
      // anything else (a transient adapter error, or the native adapter's
      // missing-attachment-API failure shape) must NOT instantly drop a
      // possibly recoverable photo — rethrow; the caller keeps the item
      // queued and the give-up rule bounds the retries
      throw err;
    }
  }

  private addLocalPreview(path: string, blob: Blob) {
    if (!this.localPreviewUrls.has(path)) {
      this.localPreviewUrls.set(path, URL.createObjectURL(blob));
    }
  }

  private removeLocalPreview(path: string) {
    const url = this.localPreviewUrls.get(path);
    if (url) {
      URL.revokeObjectURL(url);
      this.localPreviewUrls.delete(path);
    }
  }

  /** How many uploads are still queued. Consumed by the sign-out guard so a
   * non-empty outbox blocks a data-destroying sign-out (#3988 F1). */
  async pendingCountAsync(): Promise<number> {
    return (await this.getDb().info()).doc_count;
  }

  /** Upload every queued item, oldest first. Serialized: concurrent triggers
   * (reconnect + login racing) await the drain already in flight instead of
   * double-uploading. Per-item failures leave the item queued for the next
   * drain; items past the give-up rule are dropped with telemetry. */
  async drainAsync(): Promise<void> {
    // if no user is logged in yet, skip: the boot-time reachability probe
    // publishes Online BEFORE auth completes, and a drain then would call the
    // cloud functions with no auth token — every queued item from a prior
    // session would fail with an auth error, which is not offline-classified
    // and would wrongly burn the give-up counter. the post-login kick in
    // app.component re-runs the drain once auth is ready.
    if (!this.auth.user) {
      return;
    }
    if (this.drainInProgress != null) {
      return this.drainInProgress;
    }
    const deferred = DeferredPromise.defer<void>();
    this.drainInProgress = deferred;
    try {
      await this.drainInnerAsync();
    } catch (err) {
      // drain-machinery failure (e.g. the outbox DB itself failed to read) is
      // unexpected — surface it; the queued items are untouched and the next
      // drain trigger retries. (Per-item failures are handled inside.)
      this.logging.trackSilentProblem('ImageOutboxDrainFailed', {
        name: err instanceof Error ? err.name : undefined,
        message: describeError(err),
      });
    } finally {
      deferred.resolve();
      this.drainInProgress = null;
    }
  }

  /** The actual drain pass: one sequential walk over all queued items,
   * oldest first. Only ever called by drainAsync under its single-flight
   * guard. */
  private async drainInnerAsync(): Promise<void> {
    const db = this.getDb();
    const all = await db.allDocs({ include_docs: true });
    const docs = all.rows
      .map((row) => row.doc)
      .filter((doc): doc is OutboxDoc & PouchDB.Core.ExistingDocument<OutboxDoc> => doc != null)
      // oldest first: the longest-waiting photo uploads first
      .sort((a, b) => a.createdAt_utc.localeCompare(b.createdAt_utc));

    for (const doc of docs) {
      const ageDays = DateTime.utc().diff(DateTime.fromISO(doc.createdAt_utc)).as('days');
      // give-up rule first: an item already past the caps is dropped, not retried
      if (shouldDropOutboxItem({ attempts: doc.attempts, ageDays })) {
        await this.dropItemAsync(doc, ageDays);
        continue;
      }
      // whether a failure below came from the PUT, which the global fetch wrapper no longer
      // online-checks (it is marked as caller-reported), rather than from a step before or after it
      let putInFlight = false;
      try {
        // read the bytes first: an item whose bytes are confirmed missing
        // (legacy attachment doc on native — the adapter never stored them)
        // can never upload, so it is dropped as data loss instead of failing
        // forever. a THROWN read error is different: the per-item catch below
        // keeps the item queued for the next drain.
        const blob = await this.readQueuedBlobAsync(doc);
        if (blob == null) {
          await this.dropItemAsync(doc, ageDays);
          continue;
        }
        // same three-step upload as the online path in ImageService.uploadImage,
        // minus the download-URL fetch (the caller already has the final URL)
        const { signedUrl } = await this.cloudFunctions.storageGetUploadUrlAsync({
          path: doc._id,
          contentType: doc.contentType,
        });
        // reconstruct a File: Capacitor's native http bridge only serializes a
        // binary body that is `instanceof File` (see uploadImage / #3707 notes)
        const file = new File([blob], 'image.jpg', { type: doc.contentType });
        putInFlight = true;
        // console.error-only shape is fine here: a failed PUT stays queued and
        // retries on the next drain; terminal failures surface via the drop path
        await putImageToSignedUrl(signedUrl, file, doc._id, (err) =>
          console.error('ImageUploadOutbox: PUT failed, item stays queued', doc._id, err));
        putInFlight = false;
        await db.remove(doc);
        // the bytes are on the server now — the public URL works, so the
        // local preview is no longer needed
        this.removeLocalPreview(doc._id);
        this.telemetry.trackEvent('image_outbox_drained', {
          path_prefix: pathPrefix(doc._id),
          attempts: doc.attempts,
          age_ms: Math.round(DateTime.utc().diff(DateTime.fromISO(doc.createdAt_utc)).as('milliseconds')),
        });
      } catch (err) {
        // expected: pouch conflict when the user removed this photo while the
        // drain was mid-item (removeQueuedAsync deleted the doc under us) —
        // the item is gone, nothing to retry or count
        if (isPouchConflict(err)) {
          console.log('ImageUploadOutbox: item changed/removed during drain, skipping', doc._id);
          continue;
        }
        // the server refused to sign because something is already at this key
        // (#5016). Almost always this item's own PUT, whose response was lost.
        // It can never obtain a write URL now, so stop it rather than letting it
        // retry until it is given up on and reported as a photo lost.
        if (await this.settleAlreadyUploadedItemAsync(doc, err)) {
          continue;
        }
        // offline-classified failure: the connection dropped again — do NOT
        // count it against the item (R3.5: attempts only count reachable
        // drains). the combined predicate matters here: the per-item signed-URL
        // call throws the wrapped CloudFunctionNetworkError form, the raw PUT
        // throws bare fetch errors. anything else (HTTP error status, quota
        // rejection) increments toward the give-up cap.
        //
        // if the PUT itself was rejected, check the connection as the fetch wrapper did before
        // the PUT was marked (df-28f906a7558b): a real outage moves the app to Offline, and its
        // return to Online drains again. an HTTP error status got an answer, so it needs no check.
        const putRejected = putInFlight && !(err instanceof ImageUploadHttpError);
        const connectionState = putRejected ? await this.recheckConnectionAfterPutFailureAsync() : undefined;
        const transient = isOfflineOrTransientNetworkError(err);
        // if a rejection the transient check does not recognise happened while online, report
        // it: the fetch wrapper filed it as a Fetch error before the PUT was marked
        if (shouldReportOutboxPutRejection({ putRejected, transient, foundOffline: connectionState === ConnectionState.Offline })) {
          this.logging.trackSilentProblem('ImageOutboxPutRejected', { path: doc._id, error: describeError(err) });
        }
        if (!transient) {
          doc.attempts += 1;
          try {
            await db.put(doc);
          } catch (putErr) {
            // expected: the same removal race on the attempts update — the
            // doc is gone/changed, so there is nothing to count against
            if (!isPouchConflict(putErr)) {
              throw putErr;
            }
          }
        }
        console.log('ImageUploadOutbox: drain attempt failed, item stays queued',
          doc._id, 'attempts:', doc.attempts, err);
        // continue with the next item — one broken item must not starve the rest
      }
    }
  }

  /** Runs the online check after a rejected PUT (df-28f906a7558b) and returns what it found, or
   *  undefined when the check itself failed. That failure is reported, never allowed to stop the
   *  drain. */
  private async recheckConnectionAfterPutFailureAsync(): Promise<ConnectionState | undefined> {
    try {
      return await this.onlineState.verifyOnlineStatus(ConnectionState.Offline, 'suspected');
    } catch (checkErr) {
      this.logging.trackSilentProblem('ImageUploadConnectionRecheckFailed', { error: describeError(checkErr) });
      return undefined;
    }
  }

  /**
   * Settle a queued item the server refused because its key is already taken
   * (#5016). Returns true when the item has been dealt with and the drain
   * should move on; false for every other failure, which the caller then
   * handles by its ordinary retry path.
   *
   * The realistic case by far is this item's own PUT having reached the bucket
   * while its response was lost. Left queued it would fail on every drain, burn
   * its attempts and end up reported as a photo LOST — which it is not: the
   * bytes are in the bucket and the URL the document already holds resolves.
   * So the item stops retrying, and the condition is reported once under a name
   * that says exactly what is known.
   *
   * It deliberately does NOT claim to know whose object it is — see
   * `isObjectAlreadyStoredError` for why reading it back cannot answer that on
   * the web build. Whoever's it is, there is nothing this device can do about
   * it: the server will not sign a write, and the document already holds the
   * URL. The only choice is whether to keep retrying, and retrying is worse.
   *
   * **This must never throw.** It is called from INSIDE the drain's per-item
   * catch, and a throw raised in a catch block is not caught by its own try —
   * it would escape the item loop and abandon every remaining queued item. The
   * removal race this file guards against elsewhere is live here too: the user
   * can remove the photo while this runs, which deletes the doc underneath it.
   */
  private async settleAlreadyUploadedItemAsync(
    doc: OutboxDoc & PouchDB.Core.ExistingDocument<OutboxDoc>,
    err: unknown,
  ): Promise<boolean> {
    if (!isObjectAlreadyStoredError(err, STORAGE_WRITE_OBJECT_EXISTS_HTTP_STATUS)) {
      return false;
    }
    try {
      await this.getDb().remove(doc);
      this.removeLocalPreview(doc._id);
    } catch (removeErr) {
      // expected: the user removed this photo while the drain was mid-item, so
      // the doc is already gone. Either way the item is settled — there is
      // nothing left to retry — so report it and tell the caller to move on.
      if (!isPouchConflict(removeErr) && !isPouchNotFound(removeErr)) {
        console.error('ImageUploadOutbox: could not remove a settled item', doc._id, describeError(removeErr));
      }
    }
    this.logging.trackSilentProblem('ImageOutboxKeyAlreadyStored', {
      pathPrefix: pathPrefix(doc._id),
      attempts: doc.attempts,
    });
    return true;
  }

  /** Remove an item that hit the give-up rule and emit the drop telemetry
   * (Mixpanel volume + Sentry, because a drop is user-visible data loss). */
  private async dropItemAsync(doc: OutboxDoc & PouchDB.Core.ExistingDocument<OutboxDoc>, ageDays: number): Promise<void> {
    await this.getDb().remove(doc);
    this.removeLocalPreview(doc._id);
    this.telemetry.trackEvent('image_outbox_dropped', {
      path_prefix: pathPrefix(doc._id),
      attempts: doc.attempts,
      age_days: Math.round(ageDays * 10) / 10,
    });
    // a dropped photo is user-visible data loss (the document keeps pointing at
    // a URL that will never resolve) — this must reach the dev team
    this.logging.trackSilentProblem('ImageOutboxItemDropped', {
      path: doc._id,
      attempts: doc.attempts,
      ageDays: Math.round(ageDays * 10) / 10,
    });
  }
}

// Telemetry gets a COARSE label for the object key — enough to see which
// feature queues or drops, and nothing that identifies a person.
//
// The owner segment is what forces this to be a fixed label rather than "the
// folder part of the key". Since #5016 an owned key is
// `images/o/<owner token>/<collection>/<file>`, and that token is a pure
// function of the uid — so returning the leading segments would put a stable
// per-user identifier into an event property on every queue and every drop.
// A legacy key has no token, but it carries a document id in the same position
// (`images/<food id>/<file>`), which is no better.
function pathPrefix(path: string): string {
  return path.startsWith('images/') ? 'images' : 'other';
}

// Blob -> base64 conversion for the bytesBase64 doc field (no data-URL prefix
// stored). FileReader.readAsDataURL is the one Blob reader that works in every
// WebView this app runs in. The decode direction (base64ToBlob) lives in
// image-upload-outbox-rules.ts so it stays unit-testable under mocha/ts-node.
// the native image cache (#5692) writes its files with it too.
export function blobToBase64Async(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.slice(dataUrl.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
